import { computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref, useContext, useCssModule, watch, } from '@nuxtjs/composition-api';
import { ADFOX_CODES_DESKTOP } from '~/components/Adfox';
import { useA11y } from '~/composables';
export default defineComponent({
    name: 'ArticleSlider',
    props: {
        items: {
            default: () => [],
            type: Array,
        },
    },
    setup(props) {
        /**
         * Accessibility
         */
        const a11y = useA11y();
        /**
         * CSS Modules.
         */
        const css = useCssModule();
        /**
         * Context.
         */
        const context = useContext();
        /**
         * Viewport.
         */
        const viewport = context.$viewport;
        const adfoxCounter = ref(0);
        const timeout = ref();
        /**
         * Adfox partner items
         */
        const adfoxItems = {
            article_slider_1: reactive({
                config: ADFOX_CODES_DESKTOP.article_slider_1,
                isVisible: true,
            }),
            article_slider_2: reactive({
                config: ADFOX_CODES_DESKTOP.article_slider_2,
                isVisible: true,
            }),
            article_slider_3: reactive({
                config: ADFOX_CODES_DESKTOP.article_slider_3,
                isVisible: true,
            }),
            article_slider_4: reactive({
                config: ADFOX_CODES_DESKTOP.article_slider_4,
                isVisible: true,
            }),
        };
        const shouldShowSlider = computed(() => {
            return props.items.length || Object.values(adfoxItems).some((item) => item.isVisible);
        });
        const sliderElement = ref();
        const itemsElement = ref();
        const withButtons = ref(true);
        /**
         * Scroll state.
         */
        const scroll = reactive({
            current: -1,
            width: 0,
        });
        const canScrollBackward = computed(() => scroll.current > 0);
        const canScrollForward = computed(() => scroll.current < scroll.width - 32);
        /**
         * Handles "scroll" event.
         */
        function handleScroll() {
            scroll.current = itemsElement.value?.scrollLeft || 0;
        }
        /**
         * Handles completing adfox
         */
        function onAdfoxComplete(item, { error = {}, state }) {
            if (error?.message || (state.isCompleted && !state.isRendered)) {
                item.isVisible = false;
                adfoxCounter.value++;
            }
        }
        /**
         * Scroll's to a previous item.
         */
        function scrollBackward() {
            if (!canScrollBackward || !itemsElement.value) {
                return;
            }
            const { children } = itemsElement.value;
            let i = children.length;
            while (i--) {
                const child = children[i - 1];
                if (!child) {
                    continue;
                }
                if (scroll.current < child.offsetLeft + child.offsetWidth - 16) {
                    continue;
                }
                const left = child.offsetLeft;
                return itemsElement.value.scrollTo({ behavior: 'smooth', left });
            }
        }
        /**
         * Scroll's to a next item.
         */
        function scrollForward() {
            if (!canScrollForward || !itemsElement.value) {
                return;
            }
            const { children } = itemsElement.value;
            for (let i = 0, l = children.length; i < l; i++) {
                const child = children[i];
                if (scroll.current > child.offsetWidth + child.offsetLeft - 16) {
                    continue;
                }
                const nextChild = children[i + 1];
                if (!nextChild) {
                    continue;
                }
                let left = nextChild.offsetLeft;
                if (scroll.width - scroll.current < nextChild.offsetWidth) {
                    left = scroll.width;
                }
                return itemsElement.value.scrollTo({ behavior: 'smooth', left });
            }
        }
        function initialize() {
            if (!itemsElement.value || !sliderElement.value) {
                return;
            }
            itemsElement.value.scrollTo({ left: 0 });
            if (viewport.breakpoint.includes('mobile')) {
                return itemsElement.value.removeEventListener('scroll', handleScroll);
            }
            itemsElement.value.addEventListener('scroll', handleScroll);
            const { scrollWidth } = itemsElement.value;
            const { offsetWidth } = sliderElement.value;
            scroll.current = 0;
            scroll.width = scrollWidth - offsetWidth;
            withButtons.value = scrollWidth > offsetWidth;
        }
        onBeforeUnmount(() => {
            itemsElement.value?.removeEventListener('scroll', handleScroll);
        });
        onMounted(() => {
            watch([() => viewport.breakpoint, () => adfoxCounter.value, () => props.items], () => {
                clearTimeout(timeout.value);
                timeout.value = setTimeout(initialize, 500);
            }, {
                immediate: true,
            });
        });
        return {
            a11y,
            adfoxCounter,
            adfoxItems,
            canScrollForward,
            canScrollBackward,
            css,
            itemsElement,
            onAdfoxComplete,
            scroll,
            scrollBackward,
            scrollForward,
            shouldShowSlider,
            sliderElement,
            withButtons,
        };
    },
});
